import React, { useEffect, useState } from 'react';
import { Avatar, SizeValue } from '@fluentui/react-northstar';
import { Person, PersonViewType } from '@microsoft/mgt-react';
import { getCachedGroupPhoto } from '../../shared/cache/UserPhoto';
import { GroupScopeIcon, TeamScopeIcon } from './ScopeIcons';
import { ResolvedScopeMember, ScopeMemberKind } from './scopedMembers';
import { AVATAR_SIZE } from './availableToWidth';

export { AVATAR_SIZE };

// Fluent sizes an avatar's initials label from the size token alone - only the
// image stretches to the root box - so styling width/height left a team falling
// back to initials drawing at the default 32px. Snap to the nearest token. #34542
const FLUENT_AVATAR_SIZES: [number, SizeValue][] = [
  [20, 'smallest'], [24, 'smaller'], [28, 'small'], [32, 'medium'], [44, 'large']
];

const fluentAvatarSize = (px: number): SizeValue =>
  FLUENT_AVATAR_SIZES.reduce((best, s) => Math.abs(s[0] - px) < Math.abs(best[0] - px) ? s : best)[1];

interface IScopeMemberAvatar {
  member: ResolvedScopeMember;
  size?: number;
}

// mgt-react assigns every prop straight onto the custom element, so a style
// object never reaches the DOM. The variables have to sit on a wrapper and
// inherit into the element and through its shadow root instead.
//
// --color is what mgt-person paints .person-root with. Without it mgt falls back
// to its light palette, which left resolved names near black on the dark theme.
// #34542
const MgtVars = ({ size, fontSize, color, children }: {
  size: number;
  fontSize?: number;
  color?: string;
  children: React.ReactNode;
}) => {
  const vars = {
    '--avatar-size': `${size}px`,
    '--avatar-size-s': `${size}px`,
    ...(fontSize ? { '--font-size': `${fontSize}px`, '--font-weight': '400' } : {}),
    ...(color ? { '--color': color } : {}),
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 0
  } as React.CSSProperties;

  return <span style={vars}>{children}</span>;
};

// One Available To avatar. Teams show their Entra group photo, falling back to
// Fluent initials on the team name. Groups always use the group icon - a support
// group has no Entra identity, so there is no photo to look up. People go
// through mgt-person so they match every other person avatar in the product.
export const ScopeMemberAvatar = ({ member, size = AVATAR_SIZE }: IScopeMemberAvatar) => {
  const [photo, setPhoto] = useState('');

  useEffect(() => {
    let active = true;
    if (member.kind === ScopeMemberKind.Team && member.aadObjectId) {
      getCachedGroupPhoto(member.aadObjectId).then(p => { if (active) setPhoto(p); });
    } else {
      setPhoto('');
    }
    return () => { active = false; };
  }, [member.kind, member.aadObjectId]);

  if (member.kind === ScopeMemberKind.Person) {
    return (
      <MgtVars size={size}>
        <Person userId={member.aadObjectId} view={PersonViewType.avatar} />
      </MgtVars>
    );
  }

  if (member.kind === ScopeMemberKind.Team) {
    // Nothing to draw initials from - fall back to the icon design supplied.
    if (!photo && !member.name) return <TeamScopeIcon size={size} title={member.name} />;

    return (
      <Avatar
        image={photo || undefined}
        name={member.name}
        size={fluentAvatarSize(size)}
      />
    );
  }

  return <GroupScopeIcon size={size} title={member.name} />;
};

// A popup row for a person: mgt-person resolves the display name, since scoped
// users are only ever stored as an Entra object id.
export const ScopePersonRow = ({ member, size = AVATAR_SIZE, fontSize = 12, color }: IScopeMemberAvatar & { fontSize?: number; color?: string }) => (
  <MgtVars size={size} fontSize={fontSize} color={color}>
    <Person userId={member.aadObjectId} view={PersonViewType.oneline} />
  </MgtVars>
);
